import { useState, useEffect } from "react";
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CalendarEvent, CalendarEventType, CalendarEventFormData, Player, ParticipantTeam, TournamentPhase } from "../api/types.tsx";
import { playerService, participantService, calendarService } from "../api/service.ts";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (event: CalendarEventFormData) => void;
  event?: CalendarEvent | null;
  initialDate?: string;
  tournamentId: number | null;
}

const EVENT_TYPES: { value: CalendarEventType; label: string }[] = [
  { value: "TOURNAMENT", label: "🏐 Partita" },
  { value: "FRIENDLY", label: "🤝 Amichevole" },
  { value: "VISITA_MEDICA", label: "🩺 Visita Medica" },
  { value: "BIRTHDAY", label: "🎁 Compleanno" },
];

export default function EventEditDialog({
  open,
  onClose,
  onSave,
  event,
  initialDate,
  tournamentId,
}: Props) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<ParticipantTeam[]>([]);
  const [phases, setPhases] = useState<TournamentPhase[]>([]);
  const [formData, setFormData] = useState<CalendarEventFormData>({
    date: initialDate || new Date().toISOString().split("T")[0],
    time: "20:00",
    entrance: "",
    type: "TOURNAMENT",
    title: "",
    location: "",
    note: "",
    playerId: undefined,
    teamAId: undefined,
    teamBId: undefined,
    phaseId: undefined,
    tournamentGroupId: undefined,
  });

  useEffect(() => {
    // Fetch players and teams when dialog opens
    const fetchData = async () => {
      try {
        const [playersData, teamsData] = await Promise.all([
          playerService.getPlayers(),
          participantService.getTeams(),
        ]);
        setPlayers(playersData);
        setTeams(teamsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    
    if (open) {
      fetchData();
    }
  }, [open]);

  // Fetch tournament phases when type is TOURNAMENT
  useEffect(() => {
    const fetchPhases = async () => {
      if (formData.type === "TOURNAMENT" && tournamentId != null) {
        try {
          const phasesData = await calendarService.getTournamentPhases(tournamentId);
          setPhases(phasesData);
        } catch (error) {
          console.error("Error fetching phases:", error);
        }
      } else {
        setPhases([]);
      }
    };
    
    if (open) {
      fetchPhases();
    }
  }, [open, tournamentId, formData.type]);

  useEffect(() => {
    if (event) {
      setFormData({
        date: event.date,
        time: event.time || "",
        entrance: event.entrance || "",
        type: event.type as CalendarEventType,
        title: event.title,
        location: event.location || "",
        note: event.note || "",
        playerId: event.playerId,
        teamAId: event.teamAId,
        teamBId: event.teamBId,
        phaseId: event.phaseId,
        tournamentGroupId: event.tournamentGroupId,
        opponentName: event.opponentName,
        homeGame: event.homeGame ?? true,
      });
    } else if (initialDate) {
      setFormData({
        date: initialDate,
        time: "20:00",
        entrance: "",
        type: "TOURNAMENT",
        title: "",
        location: "",
        note: "",
        playerId: undefined,
        teamAId: undefined,
        teamBId: undefined,
        phaseId: undefined,
        tournamentGroupId: undefined,
        opponentName: "",
        homeGame: true,
      });
    }
  }, [event, initialDate, open]);

  const handleChange = (field: keyof CalendarEventFormData, value: string | number | boolean | undefined) => {
    // Reset tournamentGroupId when phase changes
    if (field === 'phaseId') {
      const numValue = value ? Number(value) : undefined;
      setFormData((prev) => ({ ...prev, phaseId: numValue, tournamentGroupId: undefined }));
    } else if (field === 'tournamentGroupId') {
      const numValue = value ? Number(value) : undefined;
      setFormData((prev) => ({ ...prev, [field]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Get groups for selected phase
  const selectedPhase = phases.find(p => p.phaseId === formData.phaseId);
  const groups = selectedPhase?.groups || [];

  const handleSubmit = () => {
    onSave({
      ...formData,
      tournamentId: tournamentId ?? undefined,
    });
  };

  const isEditing = !!event;
  
  // Check if event type requires player selection (BIRTHDAY or VISITA_MEDICA)
  const needsPlayer = formData.type === "BIRTHDAY" || formData.type === "VISITA_MEDICA";
  // Check if event type requires team selection (TOURNAMENT)
  const needsTeams = formData.type === "TOURNAMENT";
  const isFriendly = formData.type === "FRIENDLY";

  // Validation: giocatore per BIRTHDAY/VISITA_MEDICA, squadre per TOURNAMENT, avversario per FRIENDLY
  const isValid = formData.date && (
    (needsPlayer && formData.playerId) ||
    (needsTeams && formData.teamAId && formData.teamBId) ||
    (isFriendly && (formData.teamBId || (formData.opponentName ?? "").trim())) ||
    (!needsPlayer && !needsTeams && !isFriendly)
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ backgroundColor: "primary.main" }}>
        {isEditing ? "Modifica Evento" : "Nuovo Evento"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tipo Evento"
            select
            value={formData.type}
            onChange={(e) => handleChange("type", e.target.value)}
            fullWidth
            disabled={isEditing}
          >
            {EVENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Data"
            type="date"
            value={formData.date}
            onChange={(e) => handleChange("date", e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          {/* Player selection for BIRTHDAY and VISITA_MEDICA */}
          {needsPlayer && (
            <TextField
              label="Giocatore"
              select
              value={formData.playerId || ""}
              onChange={(e) => handleChange("playerId", e.target.value ? Number(e.target.value) : undefined)}
              fullWidth
              required
            >
              <MenuItem value="">
                <em>Seleziona giocatore</em>
              </MenuItem>
              {players.map((player) => (
                <MenuItem key={player.id} value={player.id}>
                  {player.nickname || `${player.firstName} ${player.lastName}`}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Amichevole: avversario del 2.32 (dall'elenco) o nome libero, casa/trasferta, ora e luogo */}
          {isFriendly && (
            <>
              <Autocomplete
                freeSolo
                options={teams}
                getOptionLabel={(t) => (typeof t === "string" ? t : t.name)}
                value={teams.find((t) => t.id === formData.teamBId) ?? formData.opponentName ?? ""}
                onChange={(_, v) => {
                  if (v && typeof v !== "string") {
                    setFormData((prev) => ({ ...prev, teamBId: v.id, opponentName: v.name }));
                  }
                }}
                onInputChange={(_, text, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setFormData((prev) => ({ ...prev, opponentName: text, teamBId: undefined }));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Squadra avversaria" required
                    helperText="Scegli una squadra del torneo oppure scrivi il nome" />
                )}
              />
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={formData.homeGame === false ? "away" : "home"}
                onChange={(_, v) => v && handleChange("homeGame", v === "home")}
              >
                <ToggleButton value="home">🏠 In casa</ToggleButton>
                <ToggleButton value="away">🚌 In trasferta</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label="Ora inizio"
                type="time"
                value={(formData.time || "").substring(0, 5)}
                onChange={(e) => handleChange("time", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Luogo"
                value={formData.location}
                onChange={(e) => handleChange("location", e.target.value)}
                fullWidth
                placeholder={formData.homeGame === false ? "Palestra degli avversari" : "Vuoto = la nostra palestra"}
              />
            </>
          )}

          {/* Team selection for TOURNAMENT (Partita) */}
          {needsTeams && (
            <>
              <TextField
                label="Fase"
                select
                value={formData.phaseId || ""}
                onChange={(e) => handleChange("phaseId", e.target.value ? Number(e.target.value) : undefined)}
                fullWidth
              >
                <MenuItem value="">
                  <em>Seleziona fase</em>
                </MenuItem>
                {phases.map((phase) => (
                  <MenuItem key={phase.phaseId} value={phase.phaseId}>
                    {phase.phaseName}
                  </MenuItem>
                ))}
              </TextField>

              {formData.phaseId && groups.length > 0 && (
                <TextField
                  label="Girone/Gruppo"
                  select
                  value={formData.tournamentGroupId || ""}
                  onChange={(e) => handleChange("tournamentGroupId", e.target.value ? Number(e.target.value) : undefined)}
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Seleziona girone</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.groupId} value={group.groupId}>
                      {group.groupName} ({group.groupLabel})
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                label="Squadra A"
                select
                value={formData.teamAId || ""}
                onChange={(e) => handleChange("teamAId", e.target.value ? Number(e.target.value) : undefined)}
                fullWidth
                required
              >
                <MenuItem value="">
                  <em>Seleziona squadra</em>
                </MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id} disabled={team.id === formData.teamBId}>
                    {team.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Squadra B"
                select
                value={formData.teamBId || ""}
                onChange={(e) => handleChange("teamBId", e.target.value ? Number(e.target.value) : undefined)}
                fullWidth
                required
              >
                <MenuItem value="">
                  <em>Seleziona squadra</em>
                </MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id} disabled={team.id === formData.teamAId}>
                    {team.name}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          {isEditing ? "Salva Modifiche" : "Crea Evento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
